import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRelayChat } from '@inference.engine/agentic-react';
import { HtmlWidgetSchema, type HtmlWidget } from '@inference.engine/widgets/web';
import { createReasonerTransport } from '@/lib/api/reasoner-transport';
import { createChatId } from '@/shared/api/agent-runner';
import { useCurrentWorkspaceId } from '@/entities/workspace';

/** The relay agent that reads a campaign's delivery and emits one HTML dashboard widget. */
const MEDIA_DELIVERY_AGENT_ID = 'media-delivery-agent';

type Status = 'idle' | 'running' | 'ready' | 'empty' | 'error';

/**
 * Runs the media-delivery relay agent for one campaign and surfaces the dashboard widget it
 * emits, for rendering inline rather than in the chat side panel.
 *
 * On the low-level `useRelayChat` rather than `useAiChat`: there is no chat here, only a
 * structured send (`input: { campaignId }`) and the one `data-widget` event the agent returns.
 * The transport binds the context into its URL, so the context is minted first and the transport
 * rebuilt around it; the pending campaign is held until that context exists, then sent. The
 * widget's `data` parses with `HtmlWidgetSchema`, the same shape `HtmlWidgetPanel` reads.
 */
export function useGenerateDashboard() {
  const workspaceId = useCurrentWorkspaceId();
  const [contextId, setContextId] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [widget, setWidget] = useState<HtmlWidget | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The campaign to run once the context for it is ready, since the send waits on the context.
  const pendingCampaignId = useRef<string | null>(null);
  // Whether this run produced a widget, so a run that finishes with only text (a campaign with
  // no delivery) resolves to 'empty' rather than spinning on 'running' forever.
  const gotWidget = useRef(false);
  // Whether the stream has actually begun. `chat.status` reads 'ready' both before a run starts
  // and after it ends, so the empty verdict waits until a run has genuinely streamed — otherwise
  // the gap between minting the context and the first chunk is misread as "finished, no widget".
  const hasStreamed = useRef(false);

  const transport = useMemo(
    () =>
      // 'none' is a URL placeholder before the real context is minted, not user-facing copy.
      // eslint-disable-next-line i18next/no-literal-string
      createReasonerTransport({ contextId: contextId ?? 'none', workspaceId: workspaceId ?? '' }),
    [contextId, workspaceId],
  );

  const chat = useRelayChat({
    transport,
    onData: (part) => {
      if (part.type !== 'data-widget') return;
      // `part.data` is untyped network JSON; the agent puts the HtmlWidget at `.widget.data`,
      // the same place `useAiChat` reads `event.data.widget` from.
      const payload = (part.data as { widget?: { data?: unknown } } | undefined)?.widget?.data;
      const parsed = HtmlWidgetSchema.safeParse(payload);
      if (parsed.success) {
        gotWidget.current = true;
        setWidget(parsed.data);
        setStatus('ready');
      }
    },
  });

  // Mark that a run has begun, so the empty verdict below cannot fire on the pre-stream 'ready'.
  useEffect(() => {
    if (chat.status === 'submitted' || chat.status === 'streaming') hasStreamed.current = true;
  }, [chat.status]);

  // A run that has streamed and returned to 'ready' without a widget is a campaign with no
  // delivery to chart — resolve to 'empty' so the page stops loading.
  useEffect(() => {
    if (
      status === 'running' &&
      chat.status === 'ready' &&
      hasStreamed.current &&
      !gotWidget.current
    ) {
      setStatus('empty');
    }
  }, [status, chat.status]);

  // A stream/transport error sets chat.status to 'error' without rejecting sendMessage, so the
  // send's catch never fires for it — surface it here, or the run spins on 'running' forever.
  // A null message lets the consumer fall back to its own translated copy.
  useEffect(() => {
    if (status === 'running' && chat.status === 'error') {
      setError(chat.error?.message ?? null);
      setStatus('error');
    }
  }, [status, chat.status, chat.error]);

  // Send only once the transport has the real context in its URL.
  useEffect(() => {
    const campaignId = pendingCampaignId.current;
    if (!contextId || !campaignId) return;
    pendingCampaignId.current = null;
    void chat
      .sendMessage(
        { text: '' },
        { body: { agentId: MEDIA_DELIVERY_AGENT_ID, input: { campaignId } } },
      )
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : null);
        setStatus('error');
      });
    // chat is recreated each render; the context change is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextId]);

  const generate = useCallback(async (campaignId: string) => {
    setStatus('running');
    setWidget(null);
    setError(null);
    gotWidget.current = false;
    hasStreamed.current = false;
    pendingCampaignId.current = campaignId;
    try {
      const context = await createChatId(MEDIA_DELIVERY_AGENT_ID, undefined, true);
      // A new id each run so a repeat generate is a fresh context, not an append.
      setContextId(context.id);
    } catch (cause) {
      pendingCampaignId.current = null;
      setError(cause instanceof Error ? cause.message : null);
      setStatus('error');
    }
  }, []);

  return { generate, widget, status, error };
}
